import React, { useEffect, useState } from 'react'
import DateFormat from '../../shared/helpers/date-format'
import CapacityMessage from '../../shared/components/capacity-message'
import { anyMembershipTickets, isStandalone } from '../helpers/logic'
import UpdateCard from './update-card'
import TicketTypes from './ticket-types'
import { Loading } from '../../shared/components'
import { getRemainingTickets } from '../../services/api'
import {
  hasMemberTickets,
  ticketsWithConstituencyPricing
} from '../../shared/helpers/logic'
import OrderNote from './order-note'
import OrderReferrer from './order-referrer'
import { has, sumBy } from 'lodash-es'
const { timeSimple } = DateFormat

const membershipOptionsAvailable = (item, cartHasMembership, membership) => {
  return (
    (membership.active || cartHasMembership) &&
    !anyMembershipTickets(item.tickets)
  )
}

function EventItem({
  items,
  item,
  cartHasMembership,
  membership,
  dependencies,
  showSingleUpdateLink,
  updateNeeded,
  adjustTickets
}) {
  const [loading, setLoading] = useState(true)
  const [remainingTickets, setRemainingTickets] = useState({})
  let memberPricingTicketsExist = has(item.pricing, 'member')

  let name = item.name
  if (item.tickets.some(ticket => ticket.zoneId > 0)) {
    name = item.tickets[0].zoneTime + ' ' + name
  }

  if (item.type.includes('scheduled') || item.type === 'standalone') {
    name = `${timeSimple(item.occursAt)} ${name}`
  }
  let itemDependencies = isStandalone(item) ? [] : dependencies
  let showUpdateLink =
    isStandalone(item) && !showSingleUpdateLink && memberPricingTicketsExist

  const ticketsWithConstituencyPricingIds = ticketsWithConstituencyPricing(
    item.tickets
  ).map(t => t.priceTypeId)

  const fetchRemainingTickets = async eventId =>
    (await ticketsWithConstituencyPricingIds.length) > 0
      ? getRemainingTickets(
          eventId,
          item.nativeEventId,
          ticketsWithConstituencyPricingIds
        )
      : getRemainingTickets(eventId)

  useEffect(() => {
    let subscribed = true
    setLoading(true)
    fetchRemainingTickets(item.externalId)
      .then(data => {
        if (subscribed) {
          setRemainingTickets(data)
          setLoading(false)
        }
      })
      .catch(e => {
        console.warn(e)
        setLoading(false)
      })
  }, [])

  const hasNoRemainingMemberTickets =
    remainingTickets.remainingMembershipTickets === 0

  const itemHasMemberTickets = hasMemberTickets(item.tickets)

  if (loading) {
    return <Loading />
  }

  const cartTotal = sumBy(item.tickets, ticket => ticket.quantity)
  // item.availability and item.cartTotal are fixed when the cart app receives them
  // `availability` has the "held" tickets in the cart removed, the quantity
  // of those held tickets is `cartTotal`
  // we can add them together to know what the event's total availability is
  const shouldRenderMaxMessage = cartTotal >= item.availability + item.cartTotal
  const shouldRenderLimitMessage =
    item.quantityLimit && cartTotal >= item.quantityLimit

  const eventCapacityMessage =
    'Event at capacity. You have reserved all the remaining tickets for the selected event.'
  const eventLimitMessage =
    'You have reserved the maximum tickets allowed for this event. Please call 312-939-2438 to inquire about additional tickets.'

  let maxMessage

  if (shouldRenderMaxMessage && shouldRenderLimitMessage)
    maxMessage = eventLimitMessage
  else if (shouldRenderLimitMessage) maxMessage = eventLimitMessage
  else if (shouldRenderMaxMessage) maxMessage = eventCapacityMessage

  const messageContainer = (
    <div className="font-bold text-16 mb-4">{maxMessage}</div>
  )

  return (
    <div
      key={`ticket-${item.id}`}
      className="bordered-content"
      data-testid={'event-item'}
    >
      <h2
        data-testid={'event-name'}
      >
        {name}
      </h2>
      <p
        dangerouslySetInnerHTML={{ __html: item.description }}
      >
        {}
      </p>
      {(shouldRenderMaxMessage || shouldRenderLimitMessage) && messageContainer}
      <CapacityMessage
        event={item}
        remainingTickets={remainingTickets}
        hasActiveMembership={membership.active}
        showMembershipMessaging={itemHasMemberTickets}
      />
      {membershipOptionsAvailable(item, cartHasMembership, membership) &&
      updateNeeded &&
      memberPricingTicketsExist &&
      !hasNoRemainingMemberTickets ? (
        <UpdateCard
          showLink={showUpdateLink}
          item={item}
          dependencies={dependencies}
          adjustTickets={adjustTickets}
        />
      ) : (
        <TicketTypes
          items={items}
          item={item}
          membership={membership}
          dependencies={itemDependencies}
          remainingTickets={remainingTickets}
        />
      )}
      {item.showNoteField && (
        <OrderNote
          forEvent={item.externalId}
          required={item.noteIsRequired}
          title={item.noteFieldTitle ? item.noteFieldTitle : 'Order Notes'}
          description={item.noteFieldDescription}
        />
      )}
      {item.showReferredBy && (
        <OrderReferrer
          itemId={item.id}
          referredByLabel={item.referredByLabel}
          referredByHint={item.referredByHint}
        />
      )}
    </div>
  )
}

export default EventItem
